import { useCallback, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useGameStore } from '@stores/gameStore';

// Orchestrator URL - separate service for crash game multiplayer
const ORCHESTRATOR_URL = import.meta.env['VITE_ORCHESTRATOR_URL'] as string | undefined;

interface RGSHookResult {
  connect: (token: string, operatorId?: string, orchestratorUrl?: string | null) => void;
  disconnect: () => void;
  placeBet: (amount: number, autoCashout?: number) => void;
  cashOut: () => void;
  isConnecting: boolean;
}

// Orchestrator event payloads
interface RoundStartingPayload {
  roundId: string;
  seedHash: string;
  bettingDurationMs: number;
  bettingEndsAt: number;
}

interface BettingOpenPayload {
  roundId: string;
  bettingEndsAt: number;
  remainingMs: number;
}

interface MultiplierUpdatePayload {
  roundId: string;
  multiplier: number;
  elapsedMs: number;
  autoCashedOut: Array<{
    betId: string;
    playerId: string;
    cashoutMultiplier: number;
    winAmount: number;
  }>;
}

interface CrashedPayload {
  roundId: string;
  crashMultiplier: number;
  seed: string;
  elapsedMs: number;
}

interface JoinResponsePayload {
  success: boolean;
  currentRound?: {
    roundId: string;
    status: string;
    currentMultiplier: number;
    playerBets: Array<{ betId: string; playerId: string; betAmount: number; status: string }>;
    bettingEndsAt?: number;
  };
  message?: string;
}

interface PlaceBetResponsePayload {
  success: boolean;
  bet?: {
    betId: string;
    roundId: string;
    betAmount: number;
    autoCashoutMultiplier?: number;
    status: string;
  };
  error?: string;
}

interface CashOutResponsePayload {
  success: boolean;
  betId?: string;
  cashoutMultiplier?: number;
  winAmount?: number;
  error?: string;
}

/**
 * Hook for managing Crash Game Orchestrator WebSocket connection
 * Connects to the dedicated orchestrator service (not RGS)
 * Handles multiplayer game state, betting, and cashouts
 */
export function useRGS(): RGSHookResult {
  const socketRef = useRef<Socket | null>(null);
  const sessionIdRef = useRef<string>('');
  const currentBetIdRef = useRef<string>('');
  const isConnectingRef = useRef<boolean>(false);

  const {
    connectionStatus,
    setConnected,
    setConnectionStatus,
    setConnectionError,
    setSessionId,
    setGamePhase,
    setMultiplier,
    setCrash,
    placeBet: storePlaceBet,
    cashOut: storeCashOut,
    addCrashToHistory,
    clearBet,
    setRoundId,
    setSeedHash,
  } = useGameStore();

  const connect = useCallback(
    (token: string, _operatorId = 'default', orchestratorUrl?: string | null) => {
      if (socketRef.current?.connected) return;
      if (isConnectingRef.current) return;

      isConnectingRef.current = true;
      setConnectionStatus('connecting');

      // Generate a session ID from token (in production, this comes from RGS session init)
      sessionIdRef.current = `session-${token.slice(0, 8)}-${Date.now()}`;

      // Priority: passed orchestratorUrl > env variable > localhost fallback
      const baseUrl = orchestratorUrl ?? ORCHESTRATOR_URL ?? 'ws://localhost:3200';
      // Orchestrator uses /crash namespace
      const wsUrl = `${baseUrl}/crash`;
      console.log('[Orchestrator] Connecting to:', wsUrl);

      const socket = io(wsUrl, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
      });

      // Connection events
      socket.on('connect', () => {
        isConnectingRef.current = false;
        setConnected(true);
        setConnectionError(null);
        setSessionId(sessionIdRef.current);

        // Join the crash game room
        socket.emit('crash-game:join', {
          sessionId: sessionIdRef.current,
          playerId: sessionIdRef.current, // Use session as player ID for now
        });
      });

      socket.on('disconnect', (reason) => {
        setConnected(false);
        if (reason === 'io server disconnect') {
          setConnectionError('Server disconnected');
        }
      });

      socket.on('connect_error', (error) => {
        isConnectingRef.current = false;
        setConnectionError(error.message);
        setConnectionStatus('error');
      });

      // Reconnection events
      socket.io.on('reconnect_attempt', () => {
        setConnectionStatus('reconnecting');
      });

      socket.io.on('reconnect', () => {
        setConnectionStatus('connected');
        setConnectionError(null);
        // Rejoin the game room
        socket.emit('crash-game:join', {
          sessionId: sessionIdRef.current,
          playerId: sessionIdRef.current,
        });
      });

      socket.io.on('reconnect_failed', () => {
        setConnectionStatus('error');
        setConnectionError('Failed to reconnect after multiple attempts');
      });

      // === Orchestrator Events ===

      // Join response - get current round state
      socket.on('crash-game:join:response', (data: JoinResponsePayload) => {
        if (data.success) {
          console.log('[Orchestrator] Joined game room', data);
          if (data.currentRound) {
            setRoundId?.(data.currentRound.roundId);
            if (data.currentRound.status === 'BETTING') {
              setGamePhase('betting');
            } else if (data.currentRound.status === 'IN_PROGRESS') {
              setGamePhase('playing');
              setMultiplier(data.currentRound.currentMultiplier);
            }
          }
        } else {
          setConnectionError(data.message ?? 'Failed to join game');
        }
      });

      // Round starting - new round begins, betting opens
      socket.on('crash-game:round-starting', (data: RoundStartingPayload) => {
        console.log('[Orchestrator] Round starting', data);
        setRoundId?.(data.roundId);
        setSeedHash?.(data.seedHash); // For provably fair verification
        setGamePhase('betting');
        clearBet();
      });

      // Betting open - explicitly marks betting phase
      socket.on('crash-game:betting-open', (data: BettingOpenPayload) => {
        console.log('[Orchestrator] Betting open', data);
        setGamePhase('betting');
      });

      // Betting closed - no more bets
      socket.on('crash-game:betting-closed', () => {
        console.log('[Orchestrator] Betting closed');
        // Transition to countdown/starting
      });

      // Multiplier updates during round
      socket.on('crash-game:multiplier-update', (data: MultiplierUpdatePayload) => {
        setGamePhase('playing');
        setMultiplier(data.multiplier);

        // Check if our bet was auto-cashed out
        if (currentBetIdRef.current) {
          const ourCashout = data.autoCashedOut.find((c) => c.betId === currentBetIdRef.current);
          if (ourCashout) {
            console.log('[Orchestrator] Auto-cashed out!', ourCashout);
            storeCashOut(ourCashout.cashoutMultiplier, ourCashout.winAmount);
            currentBetIdRef.current = '';
          }
        }
      });

      // Round crashed
      socket.on('crash-game:crashed', (data: CrashedPayload) => {
        console.log('[Orchestrator] Crashed at', data.crashMultiplier);
        setCrash(data.crashMultiplier);
        addCrashToHistory(data.crashMultiplier);
        currentBetIdRef.current = '';
      });

      // Bet placement response
      socket.on('crash-game:place-bet:response', (data: PlaceBetResponsePayload) => {
        console.log('[Orchestrator] Bet response', data);
        if (data.success && data.bet) {
          currentBetIdRef.current = data.bet.betId;
          // Balance update would come from RGS wallet integration
        } else {
          setConnectionError(data.error ?? 'Bet failed');
          clearBet();
        }
      });

      // Cash out response
      socket.on('crash-game:cash-out:response', (data: CashOutResponsePayload) => {
        console.log('[Orchestrator] Cash out response', data);
        if (data.success && data.winAmount !== undefined) {
          storeCashOut(data.cashoutMultiplier!, data.winAmount);
          currentBetIdRef.current = '';
        } else {
          setConnectionError(data.error ?? 'Cashout failed');
        }
      });

      // Other player bet (for live feed)
      socket.on('crash-game:player-bet', (data: { playerId: string; betAmount: number }) => {
        console.log('[Orchestrator] Player bet', data);
        // Update live bet feed
      });

      // Other player cashed out (for live feed)
      socket.on('crash-game:player-cashed-out', (data: { playerId: string; cashoutMultiplier: number; winAmount: number }) => {
        console.log('[Orchestrator] Player cashed out', data);
        // Update live feed
      });

      // Server shutdown warning - graceful shutdown notification
      socket.on('crash-game:shutdown-warning', (data: { message: string; timeoutMs: number }) => {
        console.warn('[Orchestrator] Shutdown warning:', data.message, `(${data.timeoutMs}ms)`);
        // Could show a toast/modal to user about impending shutdown
        setConnectionError(`Server shutting down: ${data.message}`);
      });

      // Server shutdown - connection will be closed
      socket.on('crash-game:server-shutdown', (data: { message: string; reason?: string }) => {
        console.warn('[Orchestrator] Server shutdown:', data.message);
        setConnectionError('Server is shutting down');
      });

      // Error handling
      socket.on('error', (error: { message: string }) => {
        setConnectionError(error.message);
      });

      socketRef.current = socket;
    },
    [
      setConnected,
      setConnectionStatus,
      setConnectionError,
      setSessionId,
      setGamePhase,
      setMultiplier,
      setCrash,
      addCrashToHistory,
      clearBet,
      storeCashOut,
      setRoundId,
      setSeedHash,
    ]
  );

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('crash-game:leave', { sessionId: sessionIdRef.current });
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    isConnectingRef.current = false;
    setConnected(false);
  }, [setConnected]);

  const placeBet = useCallback(
    (amount: number, autoCashout?: number) => {
      if (!socketRef.current?.connected) {
        setConnectionError('Not connected to server');
        return;
      }

      socketRef.current.emit('crash-game:place-bet', {
        sessionId: sessionIdRef.current,
        playerId: sessionIdRef.current, // Required by orchestrator for bet ownership
        betAmount: amount,
        autoCashoutMultiplier: autoCashout,
      });
      storePlaceBet(amount, autoCashout);
    },
    [storePlaceBet, setConnectionError]
  );

  const cashOut = useCallback(() => {
    if (!socketRef.current?.connected) {
      setConnectionError('Not connected to server');
      return;
    }

    if (!currentBetIdRef.current) {
      setConnectionError('No active bet');
      return;
    }

    socketRef.current.emit('crash-game:cash-out', {
      sessionId: sessionIdRef.current,
      playerId: sessionIdRef.current, // Required by orchestrator for bet ownership validation
      betId: currentBetIdRef.current,
    });
  }, [setConnectionError]);

  // Cleanup on unmount
  useEffect(() => {
    return (): void => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    placeBet,
    cashOut,
    isConnecting: connectionStatus === 'connecting' || connectionStatus === 'reconnecting',
  };
}
